/* eslint-disable react-hooks/rules-of-hooks */
import {
  DropdownIndicatorProps,
  GroupBase,
  InputProps,
  OptionsOrGroups,
  SelectInstance,
  components,
} from "react-select";
import {
  DropdownIndicator as DropdownIndicator_,
  isGroup,
  OptionType,
  SelectInputProps,
} from "./select-input";
import React from "react";
import { useMutation } from "@tanstack/react-query";
import { cn, copyToClipboard } from "@/lib/utils";
import { BiCopy } from "react-icons/bi";
import { Indicator } from "../custom/indicator";
import { useCurrentRef } from "@/hooks/use-current-ref";

const OptionSerializations = {
  value: <Value, Option extends OptionType<Value>>(op: Option) => op.value,
  label: <Value, Option extends OptionType<Value>>(op: Option) => op.label,
} as const;

export type CopyPasteProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> = {
  copyPaste?: boolean;
  inputValidator?: (value: string) => boolean;
  delimiter?: string | RegExp;
  joiner?: string;
  getNewOptionData?: (
    inputValue: string,
    optionLabel: React.ReactNode,
  ) => Option | undefined;
  getNewOptionDataAsync?: (value: string[]) => Promise<Option[]>;
  optionSerializer?:
    | keyof typeof OptionSerializations
    | ((option: Option) => any);
  options?: OptionsOrGroups<Option, Group>;
};

export type UseCopyPasteProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> = CopyPasteProps<Value, Option, IsMulti, Group> &
  Pick<
    SelectInputProps<Value, Option, IsMulti, Group>,
    "isMulti" | "onChange" | "onKeyDown"
  >;

const useOptionSerializer = <Value, Option extends OptionType<Value>>(
  optionSerializer:
    | keyof typeof OptionSerializations
    | ((option: Option) => any) = "label",
) => {
  return typeof optionSerializer === "string"
    ? OptionSerializations?.[optionSerializer]
    : optionSerializer;
};

function asKey(v: any) {
  return typeof v !== "object" ? v : JSON.stringify(v);
}

export const _useCopyPaste = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>({
  isMulti,
  onChange,
  inputValidator,
  delimiter = /[\n\s,]+/g,
  joiner = "\n",
  getNewOptionData,
  onKeyDown: _onKeyDown,
  getNewOptionDataAsync,
  optionSerializer: _optionSerializer,
  options,
}: UseCopyPasteProps<Value, Option, IsMulti, Group>) => {
  const optionsRef = useCurrentRef(options);
  const ref = React.useRef<SelectInstance<Option, IsMulti, Group> | null>(null);
  const optionSerializer = useOptionSerializer(_optionSerializer);
  const storeOptions = React.useCallback(
    (options: Option[]) => {
      // Nothing to add leaves the value alone: a single select would otherwise be cleared.
      if (options.length === 0) return;
      if (!ref.current) {
        console.warn("can't store options, ref is null");
        return;
      }
      if (isMulti) {
        const _current = ref.current.getValue();
        const current = Array.isArray(_current) ? _current : [];
        const next = [...current, ...options] as any;
        ref.current.setValue(next, "select-option");
        if (onChange) {
          onChange(next, { action: "select-option", option: undefined });
        }
      } else {
        const next = options.at(0)! as any;
        ref.current.setValue(next, "select-option");
        if (onChange) {
          onChange(next, { action: "select-option", option: next });
        }
      }
    },
    [ref, onChange],
  );
  // Trimmed, since only the default delimiter splits on whitespace: with "\n" (names that may hold spaces), a typed
  // name keeps the spaces around it, and a line pasted from Windows its "\r". A tab always separates: it is how a row
  // of spreadsheet cells pastes, and no name holds one.
  const split = React.useCallback(
    (text: string) =>
      (isMulti && delimiter
        ? text.split(delimiter).flatMap((v) => v.split("\t"))
        : [text]
      )
        .map((v) => v.trim())
        .filter((v) => v && (!inputValidator || inputValidator(v))),
    [isMulti, delimiter, inputValidator],
  );
  // Leaves out what is already chosen, and repeats within one input, by value. A name typed in (an option whose value
  // is its label) is also matched, ignoring case, against the names of options that hold an id: the chip of a saved
  // tag holds the tag's id. Options that are their own value (email addresses, uuids) are told apart by it alone.
  const withoutChosen = React.useCallback(
    (options: (Option | undefined)[]) => {
      const isName = (op: Option) => String(op.value) === op.label;
      const values = new Set<unknown>();
      const idNames = new Set<string>();
      const add = (op: Option) => {
        values.add(asKey(op.value));
        if (!isName(op)) idNames.add(String(op.label).toLowerCase());
      };
      (ref.current?.getValue() ?? []).forEach(add);
      return options.filter((op): op is Option => {
        if (
          !op ||
          values.has(asKey(op.value)) ||
          (isName(op) && idNames.has(op.label.toLowerCase()))
        ) {
          return false;
        }
        add(op);
        return true;
      });
    },
    [ref],
  );
  const createOptions = React.useCallback(
    (text: string) =>
      getNewOptionData
        ? withoutChosen(split(text).map((v) => getNewOptionData(v, undefined)))
        : [],
    [getNewOptionData, split, withoutChosen],
  );

  // The options a text names, one per piece where it can (undefined for a piece no option has), chosen or not. Only a
  // search (getNewOptionDataAsync) answers later; the others answer at once, while a paste can still be stopped.
  const resolve = React.useCallback(
    (text: string): (Option | undefined)[] | Promise<Option[]> => {
      const items = split(text);
      if (getNewOptionDataAsync) return getNewOptionDataAsync(items);
      if (getNewOptionData) {
        return items.map((v) => getNewOptionData(v, undefined));
      }
      if (!optionsRef.current) return [];
      const optionsMap = Object.fromEntries(
        optionsRef.current.flatMap((op) =>
          isGroup(op)
            ? op.options.flatMap((o) => [
                [o.value, o],
                [o.label, o],
              ])
            : [
                [op.value, op],
                [op.label, op],
              ],
        ),
      );
      return items.map((v) => optionsMap[v]);
    },
    [getNewOptionData, getNewOptionDataAsync, split],
  );
  const createOptionsAsync = React.useCallback(
    async (text: string) => withoutChosen(await resolve(text)),
    [resolve, withoutChosen],
  );

  const isShiftRef = useCurrentRef(false);

  const onPaste: React.ClipboardEventHandler<HTMLInputElement> =
    React.useCallback(
      async (ev) => {
        const text = ev.clipboardData.getData("text");
        if (isShiftRef.current) {
          return;
        }
        // Decided before any await when the lookup answers at once: the paste's default, the text landing in the
        // search, can only be stopped while the event is being handled. A paste every piece of which is an option is
        // taken even when all of it is chosen already, or its lines would run together there into one word.
        const pending = resolve(text);
        const searched = !Array.isArray(pending);
        const found = Array.isArray(pending) ? pending : await pending;
        const additional = withoutChosen(found);
        if (
          additional.length > 0 ||
          (found.length > 0 && found.every(Boolean))
        ) {
          ev.preventDefault();
          storeOptions(additional);
          // A search answers after the paste's default has put the text in the search, its line breaks dropped by the
          // single-line input. Storing clears it, but a paste all chosen already stores nothing: clear it here, unless
          // the search has changed since.
          const select = ref.current;
          if (
            searched &&
            select &&
            select.props.inputValue === text.replace(/[\r\n]/g, "")
          ) {
            select.onInputChange("", {
              action: "set-value",
              prevInputValue: select.props.inputValue,
            });
          }
          if (ref?.current?.inputRef?.onblur) {
            (ref.current.inputRef as any).onblur(ev as any);
          }
        }
      },
      [resolve, withoutChosen, storeOptions, ref],
    );

  const copy = useMutation({
    mutationFn: async () => {
      if (!ref.current) {
        console.warn("can't copy, ref is null");
        return;
      }
      return await copyToClipboard(
        (ref.current?.getValue() ?? [])
          .map((op) => (optionSerializer ? optionSerializer(op) : op.value))
          .join(joiner),
      );
    },
  });

  const onPasteRef = useCurrentRef(onPaste);

  const Input = React.useCallback(
    (props) => (
      <components.Input
        {...props}
        onKeyDown={(ev) => {
          if ((ev.metaKey || ev.ctrlKey) && ev.shiftKey) {
            isShiftRef.current = true;
          } else {
            isShiftRef.current = false;
          }
        }}
        onKeyUp={(ev) => {
          isShiftRef.current = false;
        }}
        onPaste={(...args) => onPasteRef.current(...args)}
      />
    ),
    [onPasteRef, isShiftRef],
  ) as React.ComponentType<InputProps<Option, IsMulti, Group>>;

  const copyRef = useCurrentRef(copy);

  const DropdownIndicator = React.useCallback(
    (props: DropdownIndicatorProps<Option, IsMulti, Group>) => {
      const copyValue = (props.getValue() ?? [])
        .map((op) => (optionSerializer ? optionSerializer(op) : op.value))
        .join(joiner);

      if (!copyValue) {
        return <DropdownIndicator_ {...props} />;
      }
      return (
        <div className="flex items-center gap-1">
          <div className="relative">
            <BiCopy
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                copyRef.current.mutateAsync();
              }}
              className={cn(
                "h-3.5 w-3.5 cursor-pointer",
                "rounded-full text-muted-foreground hover:text-foreground hover:bg-ghost",
                !props.isFocused && "hidden",
              )}
            />
            {copyRef.current.isSuccess && (
              <Indicator
                position={"tr"}
                size={"2xs"}
                border={"none"}
                className="animate-flash opacity-0"
              />
            )}
          </div>
          <DropdownIndicator_ {...props} />
        </div>
      );
    },
    [copy.isSuccess, copyRef],
  );

  const isValidNewOption = React.useMemo(
    () =>
      inputValidator
        ? (text) => {
            return createOptions(text).length > 0;
          }
        : undefined,
    [inputValidator, createOptions],
  );

  const onCreateOption = React.useCallback(
    (value) => storeOptions(createOptions(value)),
    [storeOptions, createOptions],
  );

  const onKeyDown = React.useCallback(
    async (event: React.KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case "Enter":
        case "Tab": {
          event.preventDefault();
          const inputValue = ref.current?.inputRef?.value;
          if (!inputValue) {
            return;
          }
          storeOptions(await createOptionsAsync(inputValue));
          break;
        }
        default:
          if (_onKeyDown) {
            _onKeyDown(event);
          }
      }
    },
    [storeOptions, createOptionsAsync, _onKeyDown],
  );

  return {
    onKeyDown,
    onCreateOption,
    isValidNewOption,
    components: { Input, DropdownIndicator },
    copy,
    onPaste,
    createOptions,
    storeOptions,
    ref,
    delimiter: delimiter as string,
  };
};

export const useCopyPaste = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: UseCopyPasteProps<Value, Option, IsMulti, Group>,
) => {
  return (props?.copyPaste ?? true)
    ? _useCopyPaste(props)
    : ({} as Partial<
        ReturnType<typeof _useCopyPaste<Value, Option, IsMulti, Group>>
      >);
};
